import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Stethoscope, Phone, Mail, Download, Upload, Building2, BarChart3, AlertTriangle, CalendarClock, X, ClipboardList, Sparkles, ArrowUpRight, Send } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import ImportProvidersDialog from "@/components/ImportProvidersDialog";

export const SPECIALTIES = ["Psychiatrist", "PMHNP", "PsychD", "LCSW", "APRN"];
export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const EMPLOYMENT_TYPES = [
  { value: "FTE", label: "FTE · Full-time employee" },
  { value: "PT", label: "PT · Part-time" },
  { value: "IC", label: "IC · Independent contractor" },
];
const EMP_TONE = {
  FTE: "bg-slate-900 text-white border-slate-900",
  PT: "bg-amber-100 text-amber-800 border-amber-200",
  IC: "bg-rose-100 text-rose-800 border-rose-200",
};
function EmpBadge({ type, testId }) {
  if (!type) return <span className="text-xs text-slate-400 italic" data-testid={testId}>—</span>;
  return <Badge variant="outline" className={`${EMP_TONE[type] || ""} font-mono-plex text-[10px]`} data-testid={testId}>{type}</Badge>;
}
const SPEC_TONE = {
  Psychiatrist: "bg-indigo-100 text-indigo-800 border-indigo-200",
  PMHNP: "bg-emerald-100 text-emerald-800 border-emerald-200",
  PsychD: "bg-violet-100 text-violet-800 border-violet-200",
  LCSW: "bg-orange-100 text-orange-800 border-orange-200",
  APRN: "bg-sky-100 text-sky-800 border-sky-200",
};
const SPEC_GRAD = {
  Psychiatrist: "from-indigo-500 to-indigo-600",
  PMHNP: "from-emerald-500 to-emerald-600",
  PsychD: "from-violet-500 to-violet-600",
  LCSW: "from-orange-500 to-orange-600",
  APRN: "from-sky-500 to-sky-600",
};

function emptyForm() {
  return { name: "", specialty: "Psychiatrist", employment_type: "FTE", phone: "", email: "", license_number: "", license_state: "FL", license_expires: "", active: true, available_days: [], notes: "" };
}

async function downloadFile(url, filename) {
  const token = localStorage.getItem("aceh_token") || "";
  const resp = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
  const blob = await resp.blob();
  const dl = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = dl; a.download = filename; a.click(); URL.revokeObjectURL(dl);
}

function DaysPicker({ value, onChange, testIdPrefix = "days" }) {
  const toggle = (d) => {
    const has = value.includes(d);
    onChange(has ? value.filter(x => x !== d) : [...value, d]);
  };
  return (
    <div className="flex flex-wrap gap-1" data-testid={testIdPrefix}>
      {DAYS.map(d => {
        const on = value.includes(d);
        return (
          <button
            type="button" key={d} onClick={() => toggle(d)}
            data-testid={`${testIdPrefix}-${d}`}
            className={`px-2.5 py-1 text-[11px] font-mono-plex rounded-md border transition-all ${on
              ? "bg-slate-900 text-white border-slate-900 shadow-sm"
              : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
          >{d}</button>
        );
      })}
    </div>
  );
}

function DaysChips({ days }) {
  if (!days?.length) return <span className="text-xs text-slate-400 italic">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {days.map(d => <span key={d} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-mono-plex">{d}</span>)}
    </div>
  );
}

function ProviderDetailDialog({ providerId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || !providerId) { setData(null); return; }
    api.get(`/providers/${providerId}/detail`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [open, providerId]);
  if (!open) return null;
  const p = data?.provider;
  const facs = data?.facilities || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="prov-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5" /> {p?.name || "Provider"}</DialogTitle>
        </DialogHeader>
        {!data && <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>}
        {p && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={SPEC_TONE[p.specialty]}>{p.specialty}</Badge>
              <EmpBadge type={p.employment_type} testId="detail-emp" />
              <Badge className={p.active ? "bg-emerald-600 hover:bg-emerald-600" : "bg-slate-400 hover:bg-slate-400"}>{p.active ? "Active" : "Inactive"}</Badge>
              {p.available_days?.length > 0 && (
                <div className="flex items-center gap-1 text-xs text-slate-500 ml-2"><CalendarClock className="w-3 h-3" /><DaysChips days={p.available_days} /></div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {p.phone && <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-slate-400" /> {p.phone}</div>}
              {p.email && <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-slate-400" /> {p.email}</div>}
              {p.license_number && (
                <div className="col-span-2 text-xs text-slate-600 font-mono-plex">
                  License {p.license_number} · {p.license_state}
                  {p.license_expires && <span className="text-slate-400"> · exp {p.license_expires}</span>}
                </div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 mb-2 flex items-center gap-1"><Building2 className="w-3 h-3" /> {facs.length} facilit{facs.length === 1 ? "y" : "ies"} assigned</div>
              {facs.length === 0 && <div className="text-xs text-slate-400 italic py-6 text-center border border-dashed border-slate-200 rounded-md">Not yet assigned to any facility.</div>}
              <div className="space-y-2">
                {facs.map((f, i) => (
                  <Link key={i} to={`/facilities/${f.facility_id}`} onClick={() => onOpenChange(false)} className="block group" data-testid={`detail-fac-${f.facility_id}`}>
                    <Card className="p-3 border-slate-200 shadow-none transition-all group-hover:border-slate-900 group-hover:shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-slate-900 group-hover:underline">{f.name}</div>
                            <Badge variant="outline" className="text-[9px]">{f.type}</Badge>
                            <span className="text-xs text-slate-500">· {f.city}</span>
                          </div>
                          <div className="text-[11px] font-mono-plex text-slate-400 mt-0.5">covering as {f.specialty}</div>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-slate-900 transition-colors" />
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-2 text-xs">
                        <div className="rounded border border-slate-100 p-2 bg-slate-50">
                          <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Last visit</div>
                          {f.last_visit ? (
                            <>
                              <div className="font-mono-plex text-slate-900">{f.last_visit.visit_date}</div>
                              <div className="text-slate-500 truncate">{f.last_visit.purpose}</div>
                            </>
                          ) : <div className="text-slate-400 italic">No visits yet</div>}
                        </div>
                        <div className="rounded border border-slate-100 p-2 bg-slate-50">
                          <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Next follow-up</div>
                          {f.next_followup ? (
                            <>
                              <div className="font-mono-plex text-slate-900">{f.next_followup.due_date}</div>
                              <div className="text-slate-500 truncate">{f.next_followup.title}</div>
                            </>
                          ) : <div className="text-slate-400 italic">Nothing open</div>}
                        </div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Sparkline({ values }) {
  const total = (values || []).reduce((a, b) => a + b, 0);
  if (!values || values.length === 0 || total === 0) return <span className="text-xs text-slate-400" title="No visits in this window">—</span>;
  const max = Math.max(1, ...values);
  const w = 96, h = 22, step = w / Math.max(1, values.length - 1);
  const pts = values.map((v, i) => `${i * step},${h - (v / max) * (h - 4) - 2}`).join(" ");
  const last = values[values.length - 1] || 0;
  const prev = values[values.length - 2] || 0;
  const delta = last - prev;
  return (
    <div className="flex items-center gap-2" title={`${total} visits over ${values.length} weeks`}>
      <svg width={w} height={h} className="overflow-visible">
        <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-slate-900" />
        {values.map((v, i) => (
          <circle key={i} cx={i * step} cy={h - (v / max) * (h - 4) - 2} r={i === values.length - 1 ? 2.5 : 1.5} fill="currentColor" className={i === values.length - 1 ? "text-slate-900" : "text-slate-400"} />
        ))}
      </svg>
      <span className={`text-[10px] font-mono-plex ${delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-500" : "text-slate-400"}`}>
        {delta > 0 ? "▲" : delta < 0 ? "▼" : "—"} {Math.abs(delta)}
      </span>
    </div>
  );
}

export default function Providers() {
  const [rows, setRows] = useState([]);
  const [capacity, setCapacity] = useState(null);
  const [expiring, setExpiring] = useState(null);
  const [trend, setTrend] = useState(null);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [specFilter, setSpecFilter] = useState("all");
  const [empFilter, setEmpFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");

  const load = () => {
    const params = {};
    if (specFilter !== "all") params.specialty = specFilter;
    if (empFilter !== "all") params.employment_type = empFilter;
    if (activeFilter !== "all") params.active = activeFilter === "active";
    api.get("/providers", { params }).then(({ data }) => setRows(data));
    api.get("/providers/capacity").then(({ data }) => setCapacity(data)).catch(() => {});
    api.get("/providers/expiring-licenses", { params: { days: 90 } }).then(({ data }) => setExpiring(data)).catch(() => {});
    api.get("/providers/visits-trend", { params: { weeks: 6 } }).then(({ data }) => setTrend(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [specFilter, activeFilter, empFilter]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = { ...form, email: form.email || null, employment_type: form.employment_type || null };
    try {
      if (editing) await api.put(`/providers/${editing}`, payload);
      else await api.post("/providers", payload);
      toast.success(editing ? "Provider updated" : "Provider added");
      setOpen(false); setForm(emptyForm()); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const edit = (p) => { setEditing(p.provider_id); setForm({ ...emptyForm(), ...p, email: p.email || "", employment_type: p.employment_type || "FTE", license_expires: p.license_expires || "", available_days: p.available_days || [] }); setOpen(true); };

  const rowsWithCount = useMemo(() => {
    const cap = capacity?.providers || [];
    const byId = new Map(cap.map((c) => [c.provider_id, c]));
    return rows.map((r) => ({ ...r, facility_count: byId.get(r.provider_id)?.facility_count || 0, facilities: byId.get(r.provider_id)?.facilities || [] }));
  }, [rows, capacity]);

  const expiringHere = useMemo(() => {
    if (!expiring?.providers?.length) return null;
    return expiring;
  }, [expiring]);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="providers-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Clinical roster</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black flex items-center gap-2"><Stethoscope className="w-9 h-9" /> Visiting / treating providers</h1>
          <p className="text-slate-500 mt-1 text-sm">Psychiatrists, PMHNPs, PsychDs, LCSWs and APRNs on your care roster.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={specFilter} onValueChange={setSpecFilter}>
            <SelectTrigger className="w-40" data-testid="prov-filter-spec"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All specialties</SelectItem>
              {SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={empFilter} onValueChange={setEmpFilter}>
            <SelectTrigger className="w-44" data-testid="prov-filter-emp"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classifications</SelectItem>
              {EMPLOYMENT_TYPES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={activeFilter} onValueChange={setActiveFilter}>
            <SelectTrigger className="w-32" data-testid="prov-filter-active"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => downloadFile(`${API}/providers-bulk/export.xlsx`, "providers.xlsx")} data-testid="prov-export-btn">
            <Download className="w-4 h-4 mr-1" /> Export
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="prov-import-btn">
            <Upload className="w-4 h-4 mr-1" /> Import
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyForm()); setEditing(null); } }}>
            <DialogTrigger asChild><Button data-testid="prov-new-btn" className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" /> New provider</Button></DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Edit provider" : "New provider"}</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div><Label>Name</Label><Input data-testid="prov-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label>Specialty</Label>
                    <Select value={form.specialty} onValueChange={v => setForm({ ...form, specialty: v })}>
                      <SelectTrigger data-testid="prov-specialty"><SelectValue /></SelectTrigger>
                      <SelectContent>{SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Classification</Label>
                    <Select value={form.employment_type || "FTE"} onValueChange={v => setForm({ ...form, employment_type: v })}>
                      <SelectTrigger data-testid="prov-employment"><SelectValue /></SelectTrigger>
                      <SelectContent>{EMPLOYMENT_TYPES.map(e => <SelectItem key={e.value} value={e.value} data-testid={`prov-employment-${e.value}`}>{e.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Status</Label>
                    <Select value={form.active ? "active" : "inactive"} onValueChange={v => setForm({ ...form, active: v === "active" })}>
                      <SelectTrigger data-testid="prov-active"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label>License #</Label><Input value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })} /></div>
                  <div><Label>State</Label><Input value={form.license_state} onChange={e => setForm({ ...form, license_state: e.target.value })} /></div>
                  <div><Label>Expires</Label><Input type="date" value={form.license_expires} onChange={e => setForm({ ...form, license_expires: e.target.value })} /></div>
                </div>
                <div>
                  <Label className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Available days for visits</Label>
                  <div className="mt-1.5">
                    <DaysPicker value={form.available_days || []} onChange={(v) => setForm({ ...form, available_days: v })} testIdPrefix="prov-days" />
                  </div>
                </div>
                <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="prov-save" className="bg-slate-900 hover:bg-slate-800">{editing ? "Save" : "Create"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {expiringHere && (
        <Card className="p-4 mb-4 border-0 shadow-none bg-gradient-to-r from-rose-500 via-amber-500 to-orange-500 text-white" data-testid="prov-expiring-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold">Licenses expiring in the next 90 days</div>
              <div className="text-sm text-white/90 mt-0.5">
                {expiringHere.providers.length} provider{expiringHere.providers.length === 1 ? "" : "s"} need{expiringHere.providers.length === 1 ? "s" : ""} renewal action.
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {expiringHere.providers.slice(0, 6).map(x => (
                  <button
                    key={x.provider_id}
                    onClick={() => setDetailId(x.provider_id)}
                    data-testid={`exp-chip-${x.provider_id}`}
                    className="text-[11px] font-mono-plex rounded bg-white/15 hover:bg-white/25 backdrop-blur-md px-2 py-1 border border-white/25 transition-colors"
                  >
                    {x.name} · {x.specialty} · {x.expired ? "EXPIRED" : `${x.days_left}d left`}
                  </button>
                ))}
                {expiringHere.providers.length > 6 && (
                  <span className="text-[11px] font-mono-plex rounded bg-white/15 px-2 py-1 border border-white/25">+{expiringHere.providers.length - 6} more</span>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Tabs defaultValue="roster">
        <TabsList>
          <TabsTrigger value="roster" data-testid="prov-tab-roster"><Stethoscope className="w-3.5 h-3.5 mr-1" /> Roster</TabsTrigger>
          <TabsTrigger value="capacity" data-testid="prov-tab-capacity"><BarChart3 className="w-3.5 h-3.5 mr-1" /> Capacity</TabsTrigger>
        </TabsList>

        <TabsContent value="roster">
          <Card className="border-slate-200 shadow-none overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest font-mono-plex text-slate-500">
                <tr><th className="p-3">Name</th><th className="p-3">Specialty</th><th className="p-3">Class</th><th className="p-3">Contact</th><th className="p-3">License</th><th className="p-3">Available</th><th className="p-3">Workload</th><th className="p-3">Status</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {rowsWithCount.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-slate-400">No providers yet.</td></tr>}
                {rowsWithCount.map(p => (
                  <tr key={p.provider_id} className="border-t border-slate-100 row-enter" data-testid={`prov-row-${p.provider_id}`}>
                    <td className="p-3 font-medium">
                      <button onClick={() => setDetailId(p.provider_id)} data-testid={`prov-open-${p.provider_id}`} className="hover:underline text-left">
                        {p.name}
                      </button>
                    </td>
                    <td className="p-3"><Badge variant="outline" className={SPEC_TONE[p.specialty]}>{p.specialty}</Badge></td>
                    <td className="p-3"><EmpBadge type={p.employment_type} testId={`prov-emp-${p.provider_id}`} /></td>
                    <td className="p-3 text-xs">
                      {p.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" /> {p.phone}</div>}
                      {p.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" /> {p.email}</div>}
                    </td>
                    <td className="p-3 text-xs font-mono-plex text-slate-600">
                      {p.license_number ? `${p.license_number} · ${p.license_state || ""}` : "—"}
                      {p.license_expires && <div className="text-slate-400">exp {p.license_expires}</div>}
                    </td>
                    <td className="p-3" data-testid={`prov-days-cell-${p.provider_id}`}><DaysChips days={p.available_days} /></td>
                    <td className="p-3">
                      <button
                        onClick={() => setDetailId(p.provider_id)}
                        className="inline-flex items-center gap-1 text-xs font-mono-plex hover:underline"
                        data-testid={`prov-workload-${p.provider_id}`}
                      >
                        <Building2 className="w-3.5 h-3.5 text-slate-400" />
                        <span className="font-semibold text-slate-900">{p.facility_count}</span>
                        <span className="text-slate-500">facilit{p.facility_count === 1 ? "y" : "ies"}</span>
                      </button>
                    </td>
                    <td className="p-3"><Badge className={p.active ? "bg-emerald-600 hover:bg-emerald-600" : "bg-slate-400 hover:bg-slate-400"}>{p.active ? "Active" : "Inactive"}</Badge></td>
                    <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => edit(p)}>Edit</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="capacity">
          {capacity && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                {SPECIALTIES.map((s) => {
                  const b = capacity.by_specialty?.[s] || { providers: 0, active: 0, facilities: 0 };
                  return (
                    <Card key={s} className={`p-4 text-white overflow-hidden border-0 shadow-none bg-gradient-to-br ${SPEC_GRAD[s]}`} data-testid={`cap-tile-${s}`}>
                      <div className="text-[10px] uppercase tracking-widest font-mono-plex text-white/80">{s}</div>
                      <div className="font-heading font-black text-4xl leading-tight">{b.providers}</div>
                      <div className="text-[11px] text-white/85 mt-1">{b.active} active · {b.facilities} assignments</div>
                    </Card>
                  );
                })}
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4" data-testid="cap-emp-tiles">
                {EMPLOYMENT_TYPES.map((e) => {
                  const b = capacity.by_employment?.[e.value] || { providers: 0, active: 0 };
                  return (
                    <Card key={e.value} className="p-3 border-slate-200 shadow-none flex items-center justify-between" data-testid={`cap-emp-${e.value}`}>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500">{e.label}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{b.active} active</div>
                      </div>
                      <div className="font-heading font-black text-3xl">{b.providers}</div>
                    </Card>
                  );
                })}
              </div>
              <Card className="border-slate-200 shadow-none overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500">Workload report</div>
                    <h3 className="font-heading font-bold text-lg">Per-provider capacity</h3>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <div>
                      <span className="font-mono-plex font-semibold text-slate-900">{capacity.total_providers}</span> providers · <span className="font-mono-plex font-semibold text-slate-900">{capacity.total_assignments}</span> facility assignments
                    </div>
                    <Button size="sm" variant="outline" onClick={() => downloadFile(`${API}/providers-bulk/capacity-export.xlsx`, "providers-capacity.xlsx")} data-testid="cap-export-btn">
                      <Download className="w-3.5 h-3.5 mr-1" /> Export
                    </Button>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest font-mono-plex text-slate-500">
                    <tr><th className="p-3">Provider</th><th className="p-3">Specialty</th><th className="p-3">Class</th><th className="p-3">Days</th><th className="p-3">Facilities</th><th className="p-3 w-1/3">Coverage</th><th className="p-3">6-week trend</th></tr>
                  </thead>
                  <tbody>
                    {(capacity.providers || []).length === 0 && (
                      <tr><td colSpan="7" className="p-8 text-center text-slate-400">No providers yet.</td></tr>
                    )}
                    {(capacity.providers || [])
                      .filter((p) => specFilter === "all" || p.specialty === specFilter)
                      .filter((p) => empFilter === "all" || p.employment_type === empFilter)
                      .filter((p) => activeFilter === "all" || (activeFilter === "active" ? p.active : !p.active))
                      .sort((a, b) => b.facility_count - a.facility_count)
                      .map((p) => {
                        const max = Math.max(1, ...(capacity.providers || []).map(x => x.facility_count));
                        const pct = Math.round((p.facility_count / max) * 100);
                        const t = trend?.trend?.[p.provider_id] || [];
                        return (
                          <tr key={p.provider_id} className="border-t border-slate-100 row-enter" data-testid={`cap-row-${p.provider_id}`}>
                            <td className="p-3 font-medium">
                              <button onClick={() => setDetailId(p.provider_id)} className="hover:underline text-left">{p.name}</button>
                              {!p.active && <Badge className="ml-2 bg-slate-400 hover:bg-slate-400 text-[9px]">Inactive</Badge>}
                            </td>
                            <td className="p-3"><Badge variant="outline" className={SPEC_TONE[p.specialty]}>{p.specialty}</Badge></td>
                            <td className="p-3"><EmpBadge type={p.employment_type} /></td>
                            <td className="p-3"><DaysChips days={p.available_days} /></td>
                            <td className="p-3 font-mono-plex font-semibold">{p.facility_count}</td>
                            <td className="p-3">
                              {p.facility_count > 0 && (
                                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                                  <div className={`h-full bg-gradient-to-r ${SPEC_GRAD[p.specialty]}`} style={{ width: `${pct}%` }} />
                                </div>
                              )}
                              {p.facilities.length > 0 && (
                                <div className="mt-1 text-[11px] text-slate-500 truncate">{p.facilities.map(f => f.name).join(" · ")}</div>
                              )}
                            </td>
                            <td className="p-3" data-testid={`cap-trend-${p.provider_id}`}><Sparkline values={t} /></td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      <ImportProvidersDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />
      <ProviderDetailDialog providerId={detailId} open={!!detailId} onOpenChange={(v) => { if (!v) setDetailId(null); }} />
    </div>
  );
}
